import { useState } from 'react';

import { CategoryCreateEndpoint } from '@core/types/types.endpoint.category';
import { DirectoryRefreshEndpoint } from '@core/types/types.endpoint.directory';
import { Button } from '@frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import { Input } from '@frontend/components/ui/input';
import { Label } from '@frontend/components/ui/label';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface CategoryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CategoryCreateModal({
  isOpen,
  onClose,
  onSuccess,
}: CategoryCreateModalProps) {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const updateDirectory = useDirectoryUpdater();

  const submitter = useSubmitter(async () => {
    // Reset errors
    setNameError('');

    // Validate name
    if (!name.trim()) {
      setNameError('Category name is required');
      return;
    }

    if (name.trim().length < 2) {
      setNameError('Category name must be at least 2 characters');
      return;
    }

    const [error] = await callApi<CategoryCreateEndpoint>(
      '/categories/create',
      {
        name: name.trim(),
      },
    );

    if (error) {
      if (error.error.includes('already exists')) {
        setNameError('A category with this name already exists');
      } else {
        setNameError(error.error || 'Failed to create category');
      }
      return;
    }

    // Refresh categories in directory
    const [, refreshRes] = await callApi<DirectoryRefreshEndpoint>(
      '/directory/refresh',
      { categories: true },
    );
    if (refreshRes) {
      updateDirectory(refreshRes);
    }

    // Reset form
    setName('');
    setNameError('');

    onSuccess();
  });

  const handleClose = () => {
    if (!submitter.isSubmitting) {
      setName('');
      setNameError('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Category</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="category-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
              maxLength={50}
              className={nameError ? 'border-red-500' : ''}
            />
            {nameError && <p className="text-sm text-red-500">{nameError}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="mr-3" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={submitter.submit}
            disabled={!name.trim() || submitter.isSubmitting}
          >
            {submitter.isSubmitting ? 'Creating...' : 'Create Category'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
